import React, { useCallback, useEffect, useState } from "react";
import {
  Grid,
  Box,
  Toolbar,
  IconButton,
  TextField,
  Autocomplete,
  useMediaQuery,
  useTheme,
  InputAdornment,
} from "@mui/material";
import { Clear, Refresh } from "@mui/icons-material";
import { DatePicker } from "@mui/x-date-pickers";
import moment from "moment";
import { connect } from "react-redux";
import PATClearButton from "../../../components/Buttons/PATClearButton";
import debounce from "lodash.debounce";
import PATIconButton from "../../../components/Buttons/PATIconButton";
import { areAllPropsEmpty } from "../../../utils/functions";
import FilterButtons from "../../../components/Buttons/FilterButtons";
import SelectFilterDialog from "../../../components/SelectFilterDialog";
import { screenNames } from "../../../utils/constants";
import SaveFilterDialog from "../../../components/SaveFilterDialog";

const SummaryTableToolbar = ({
  onFilterChange,
  filters,
  resetFilters,
  categoryOptions,
  courseOptions,
}) => {
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down("md"));
  const [localFilters, setLocalFilters] = useState(filters);
  const [openSelectFilterDialog, setOpenSelectFilterDialog] = useState(false);
  const [openSaveFilterDialog, setOpenSaveFilterDialog] = useState(false);

  const isFilterEmpty = areAllPropsEmpty(localFilters);

  useEffect(() => {
    setLocalFilters(filters);
  }, [filters]);

  const debouncedFilterChange = useCallback(
    debounce((newFilters) => {
      onFilterChange(newFilters);
    }, 500),
    [] // only create once
  );

  const handleFilterChange = (field, value) => {
    const updatedFilters = { ...localFilters, [field]: value };
    setLocalFilters(updatedFilters);
    debouncedFilterChange(updatedFilters); // Debounced instead of immediate
  };

  const renderFilterFields = (fields) =>
    fields.map(
      ({ type, label, field, options, xs = 2, multiselect = true }) => (
        <Grid item xs={isSmall ? 12 : xs} key={field}>
          {type === "text" && (
            <TextField
              label={label}
              value={localFilters[field] || ""}
              onChange={(e) => handleFilterChange(field, e.target.value)}
              fullWidth
              variant="outlined"
              size="small"
            />
          )}

          {type === "date" && (
            <DatePicker
              label={label}
              value={localFilters[field] ? moment(localFilters[field]) : null}
              onChange={(date) =>
                handleFilterChange(field, date ? date.toISOString() : null)
              }
              minDate={
                field === "courseDateTo" && localFilters.courseDateFrom
                  ? moment(localFilters.courseDateFrom)
                  : undefined
              }
              maxDate={
                field === "courseDateFrom" && localFilters.courseDateTo
                  ? moment(localFilters.courseDateTo)
                  : undefined
              }
              clearable
              clearText="Clear"
              renderInput={(params) => (
                <TextField
                  {...params}
                  fullWidth
                  variant="outlined"
                  size="small"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {localFilters[field] && (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() => handleFilterChange(field, null)}
                              size="small"
                              edge="end"
                            >
                              <Clear fontSize="small" />
                            </IconButton>
                          </InputAdornment>
                        )}
                        {params.InputProps?.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          )}

          {type === "autocomplete" && (
            <Autocomplete
              multiple={multiselect}
              options={options || []}
              value={
                multiselect
                  ? localFilters[field] || []
                  : localFilters[field] || null
              }
              onChange={(_, value) => handleFilterChange(field, value)}
              getOptionLabel={(option) => option?.label || ""}
              isOptionEqualToValue={(o, v) => o?.value === v?.value}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={label}
                  variant="outlined"
                  size="small"
                />
              )}
              fullWidth
              sx={
                multiselect
                  ? {
                      "& .MuiAutocomplete-inputRoot": {
                        flexWrap: "nowrap",
                        maxHeight: "40px",
                      },
                      "& .MuiAutocomplete-tag": {
                        maxWidth: "100%",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      },
                      "& .MuiAutocomplete-endAdornment": {
                        alignSelf: "flex-start",
                      },
                    }
                  : {}
              }
            />
          )}

          {type === "checkbox" && (
            <Box display="flex" alignItems="center" height="100%">
              <label>
                <input
                  type="checkbox"
                  checked={!!localFilters[field]}
                  onChange={(e) => handleFilterChange(field, e.target.checked)}
                />{" "}
                {label}
              </label>
            </Box>
          )}
        </Grid>
      )
    );

  const renderOptions = () => {
    const handleRefresh = () => {
      onFilterChange({ ...localFilters });
    };

    return isSmall ? (
      <Box sx={{ display: "flex", justifyContent: "center", gap: 2 }}>
        <Grid item>
          <PATClearButton title="Clear Filters" onClick={resetFilters} />
        </Grid>
        <Grid item>
          <PATIconButton
            title="Refresh"
            onClick={handleRefresh}
            icon={<Refresh color="primary" />}
          />
        </Grid>
      </Box>
    ) : (
      <Box sx={{ display: "flex", mt: 1 }}>
        <Grid item>
          <PATClearButton title="Clear Filters" onClick={resetFilters} />
        </Grid>
        <Grid item>
          <PATIconButton
            title="Refresh"
            onClick={handleRefresh}
            icon={<Refresh color="primary" />}
          />
        </Grid>
      </Box>
    );
  };

  const filterFields = [
    {
      type: "date",
      label: "Begin Date",
      field: "courseDateFrom",
      xs: 2.7,
    },
    { type: "date", label: "End Date", field: "courseDateTo", xs: 2.7 },
    {
      type: "autocomplete",
      label: "Category",
      field: "category",
      xs: 2.7,
      options: categoryOptions,
      multiselect: false,
    },
    {
      type: "autocomplete",
      label: "Course",
      field: "course",
      xs: 2.7,
      options: courseOptions,
      multiselect: false,
    },
  ];

  return (
    <>
      <Toolbar
        disableGutters
        sx={{ flexDirection: "column", alignItems: "stretch", gap: 2 }}
      >
        <Box
          sx={{
            padding: theme.spacing(1),
            paddingTop: theme.spacing(2),
            position: "relative",
            overflow: isSmall ? "auto" : "visible",
            maxHeight: isSmall ? "40vh" : "none",
          }}
        >
          <Grid container spacing={2}>
            {isSmall ? (
              <>
                <Grid item xs={12}>
                  <Grid container spacing={1}>
                    {renderFilterFields(filterFields)}
                    {renderOptions()}
                    <FilterButtons
                      onSelectFilterClick={() => setOpenSelectFilterDialog(true)}
                      onSaveFilterClick={() => setOpenSaveFilterDialog(true)}
                      isFilterEmpty={isFilterEmpty}
                    />
                  </Grid>
                </Grid>
              </>
            ) : (
              <>
                <Grid item xs={12}>
                  <Grid container spacing={1}>
                    {renderFilterFields([...filterFields])}
                    {renderOptions()}
                    <FilterButtons
                      onSelectFilterClick={() => setOpenSelectFilterDialog(true)}
                      onSaveFilterClick={() => setOpenSaveFilterDialog(true)}
                      isFilterEmpty={isFilterEmpty}
                    />
                  </Grid>
                </Grid>
              </>
            )}
          </Grid>
        </Box>
      </Toolbar>

      <SelectFilterDialog
        open={openSelectFilterDialog}
        onClose={() => setOpenSelectFilterDialog(false)}
        screenName={screenNames.trainingSummary}
        onFilterChange={onFilterChange}
      />

      <SaveFilterDialog
        open={openSaveFilterDialog}
        onClose={() => setOpenSaveFilterDialog(false)}
        screenName={screenNames.trainingSummary}
        filters={localFilters}
      />
    </>
  );
};

const mapStateToProps = (state, ownProps) => ({
  keyboard: state.keyboard,
  ...ownProps,
});

export default connect(mapStateToProps)(SummaryTableToolbar);
